/**
 * @file resolveAttachmentKeepToggle.ts
 * @description The per-file "keep on my computer" toggle's whole state matrix —
 * kept, disabled, why, and which confirmation the click opens. Consumer:
 * `AttachmentFileKeepToggle`, which owns only the write.
 *
 * Every disabled state carries a sentence, because a greyed control that does
 * not say why reads as a defect. Each sentence that redirects the reader to the
 * artifact toolbar names that button through `desktopKeepControlLabel`, so it
 * always names the label the toolbar carries in the same state: "Keep on my
 * computer" while nothing is pinned, "On my computer" once something is.
 *
 * An artifact no pin can name outranks every other reason, because the toolbar
 * control is itself disabled in that state and sending the reader to a dead
 * button explains nothing.
 *
 * `confirm` names which way the click goes, and `'none'` writes straight away.
 * Keeping asks for the artifact's *first* pin and nothing else — adding a second
 * file to a pin that already exists does not ask again. Stopping always asks,
 * because it strands a change the agent has not yet uploaded and keeping the
 * file again does not send it. Every disabled state is `'none'`, so no click can
 * open a confirmation that describes something other than what it will do.
 */

import type { DesktopPinScope } from '#types';
import { desktopKeepControlLabel } from '../../desktopSync/desktopKeepControlLabel';

export type AttachmentKeepToggleRequest = {
    /** Names the control for a screen reader; never printed into the tooltip. */
    fileName: string;
    fileUid: string;
    /** From `resolveDesktopPinPath`. `null` for an artifact no pin can name. */
    pinPath: string | null;
    signedIn: boolean;
    /** `useDesktopPins().loading`; `keep` throws while the user document is unread. */
    loading: boolean;
    /** A write of this control's is in flight. */
    busy: boolean;
    /** From `useDesktopPin`. `null` when this artifact is not pinned at all. */
    scope: DesktopPinScope | null;
};

/** Which confirmation the click opens, or `'none'` when it writes straight away. */
export type AttachmentKeepToggleConfirm = 'none' | 'keep' | 'stop';

export type AttachmentKeepToggleState = {
    /** Whether this file is kept today, whole-document scope included. */
    kept: boolean;
    disabled: boolean;
    tooltip: string;
    ariaLabel: string;
    /** The confirmation this click opens; `'none'` writes without asking. */
    confirm: AttachmentKeepToggleConfirm;
};

export const resolveAttachmentKeepToggle = ({
    fileName,
    fileUid,
    pinPath,
    signedIn,
    loading,
    busy,
    scope,
}: AttachmentKeepToggleRequest): AttachmentKeepToggleState => {
    const keptWholeDocument = scope?.kind === 'document';
    const kept = keptWholeDocument
        || (scope?.kind === 'files' && scope.fileUids.includes(fileUid));

    const ariaLabel = kept
        ? `Stop keeping ${fileName} on my computer`
        : `Keep ${fileName} on my computer`;

    const toolbarLabel = desktopKeepControlLabel(scope !== null);

    const blockedTooltip = !pinPath
        ? 'This document cannot be kept on your computer, so its files cannot be kept one at a time.'
        : !signedIn
            ? 'Sign in to keep this file on your computer.'
            : loading
                ? 'Reading what you keep on your computer.'
                : busy
                    ? 'Saving what you keep on your computer.'
                    : keptWholeDocument
                        ? `The whole document is kept on your computer, so this file already is. Change that with ${toolbarLabel} in the document toolbar.`
                        : null;

    if (blockedTooltip !== null) {
        return { kept, disabled: true, tooltip: blockedTooltip, ariaLabel, confirm: 'none' };
    }

    return {
        kept,
        disabled: false,
        tooltip: kept
            ? 'Stop keeping this file on your computer.'
            : 'Keep this file on your computer.',
        ariaLabel,
        confirm: kept ? 'stop' : scope === null ? 'keep' : 'none',
    };
};
